import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { ProductProfile as Profile } from "../../../shared/goals/profile";
import { scrollToTop, useNative } from "../../../shared/phone/media";
import type { Product } from "../../../shared/types";
import { api, ApiError } from "../../lib/api";
import { productsApi, runActive, runsApi, type LearnAnswer, type ProfileView, type RunInfo } from "../../lib/productsApi";
import { Icon } from "../../components/ui/Icon";
import { AskCard } from "./AskCard";
import { Done } from "./Done";
import { FlowHead } from "./FlowHead";
import { LearnLive } from "./LearnLive";
import { ProfileNative } from "./ProfileNative";
import { ProfileVersions } from "./ProfileVersions";
import { ProfileWalk } from "./ProfileWalk";
import { useRunWatch } from "./useRunWatch";
import { useToast } from "./useToast";
import { correctedProfile, emptyWalk, walkParts, type WalkState } from "./walk";
import "../../styles/new-product.css";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/**
 * Everything a product's profile flow knows and does, for the page on a computer and the screens on a phone:
 * the product and its profile, the walk the owner is on, the learn run, and each step's action.
 */
function useProfileFlow(id: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const carried = (location.state ?? {}) as { error?: string };
  const { toast, toastNode } = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [view, setView] = useState<ProfileView | null>(null);
  const [loadErr, setLoadErr] = useState<ApiError | null>(null);
  const [st, setSt] = useState<WalkState>(emptyWalk);
  const [busy, setBusy] = useState(false);
  const [askRun, setAskRun] = useState<RunInfo | null>(null);
  const [justConfirmed, setJustConfirmed] = useState<{ version: number; profile: Profile } | null>(null);
  const [startNote, setStartNote] = useState("");
  const [versions, setVersions] = useState(false);
  const shownCarried = useRef(false);

  const load = useCallback(async () => {
    const v = await productsApi.profile(id);
    setView(v);
    return v;
  }, [id]);

  useEffect(() => {
    let alive = true;
    setProduct(null);
    setView(null);
    setLoadErr(null);
    setSt(emptyWalk());
    setJustConfirmed(null);
    Promise.all([api.products.detail(id), productsApi.profile(id)])
      .then(([d, v]) => {
        if (!alive) return;
        setProduct(d.product);
        setView(v);
      })
      .catch((e: unknown) => alive && setLoadErr(e instanceof ApiError ? e : new ApiError(0, String(e))));
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (carried.error && !shownCarried.current) {
      shownCarried.current = true;
      toast(carried.error, false);
    }
  }, [carried.error, toast]);

  const activeRun = view?.learnRun && runActive(view.learnRun.state) ? view.learnRun.id : null;
  const watch = useRunWatch(activeRun, () => {
    void load().then((v) => {
      if (v.draft?.question) toast("It needs one answer before it reads anything.");
      else if (v.draft?.profile) toast("Draft ready. Walk through it part by part.");
      scrollToTop();
    }).catch(() => undefined);
  });

  const question = view?.draft?.question ?? null;
  const questionRun = question ? (view?.learnRun?.id ?? null) : null;
  useEffect(() => {
    if (!questionRun) return;
    let alive = true;
    runsApi
      .get(questionRun)
      .then((r) => alive && setAskRun(r.run))
      .catch(() => alive && setAskRun(null));
    return () => {
      alive = false;
    };
  }, [questionRun]);

  const learn = async (body: { note?: string; answer?: LearnAnswer }) => {
    setBusy(true);
    try {
      await productsApi.learn(id, body);
      scrollToTop();
    } catch (e) {
      toast(message(e), false);
    } finally {
      await load().catch(() => undefined);
      setBusy(false);
    }
  };

  const answer = (a: LearnAnswer) => learn({ answer: a });

  const start = () => {
    const note = startNote.trim();
    void learn(note ? { note } : {});
  };

  const confirm = async () => {
    const draft = view?.draft;
    if (!draft?.profile || busy) return;
    const corrected = correctedProfile(draft.profile, st, walkParts(draft.profile));
    setBusy(true);
    try {
      const { version } = await productsApi.confirm(id, { version: draft.version, ...(corrected ? { profile: corrected } : {}) });
      setJustConfirmed({ version, profile: draft.profile });
      toast(`Saved to your library as profile v${version}.`);
      scrollToTop();
    } catch (e) {
      toast(message(e), false);
      if (e instanceof ApiError && e.status === 409) await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save the owner's changes to a confirmed profile as its next version. On a computer that ends the visit;
   * on a phone (`stay`) the profile stays open at the new version.
   */
  const saveChanges = async (stay = false) => {
    const current = view?.confirmed;
    if (!current || !product || busy) return;
    const corrected = correctedProfile(current.profile, st, walkParts(current.profile));
    if (!corrected) {
      toast("Nothing changed.");
      return;
    }
    setBusy(true);
    try {
      const { version } = await productsApi.confirm(id, { version: current.version, profile: corrected });
      if (!stay) {
        navigate("/products", { state: { toast: `Saved. Every goal for ${product.name} reads the new version.` } });
        return;
      }
      // Why: the walk starts again from the saved words, so it reads them first.
      await load().catch(() => undefined);
      setSt(emptyWalk());
      toast(`Saved. Every goal for ${product.name} reads version ${version}.`);
    } catch (e) {
      toast(message(e), false);
      if (e instanceof ApiError && e.status === 409) await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  return {
    id,
    product,
    view,
    loadErr,
    st,
    setSt,
    busy,
    askRun,
    justConfirmed,
    startNote,
    setStartNote,
    versions,
    setVersions,
    activeRun,
    watch,
    load,
    answer,
    start,
    confirm,
    saveChanges,
    toast,
    toastNode,
  };
}

export type ProfileFlow = ReturnType<typeof useProfileFlow>;

/** The profile flow on a computer: one page, its head saying where the flow is. */
function ProfileDesktop({ flow }: { flow: ProfileFlow }) {
  const { id, product, view, loadErr, st, setSt, busy, askRun, justConfirmed, startNote, setStartNote, versions, setVersions, activeRun, watch, load, answer, start, confirm, saveChanges, toast, toastNode } =
    flow;
  if (loadErr)
    return (
      <section className="wrap ga addprod">
        <p className="load-note">{loadErr.status === 404 ? "This product does not exist, or it belongs to another account." : `Couldn't load this product: ${loadErr.message}`}</p>
      </section>
    );
  if (!product || !view)
    return (
      <section className="wrap ga addprod">
        <p className="load-note">Loading…</p>
      </section>
    );

  const crumb = view.confirmed && !justConfirmed ? "Edit profile" : "Add a product";
  const name = product.name;
  const draft = view.draft;
  let body;

  if (justConfirmed) {
    body = (
      <>
        <FlowHead stage="First goal" crumb={crumb} title="Profile confirmed" sub={`${name} is in your library. Every goal for it reads this first.`} />
        <Done productId={id} profile={justConfirmed.profile} st={st} version={justConfirmed.version} />
      </>
    );
  } else if (activeRun) {
    body = (
      <>
        <FlowHead stage="Learn" crumb={crumb} title="Learning the business" sub={product.description ? `${name} · ${product.description}` : name} />
        <LearnLive run={watch.run} events={watch.events} />
        {watch.error && <p className="load-note">Couldn't read the run: {watch.error}</p>}
      </>
    );
  } else if (draft?.question) {
    body = (
      <>
        <FlowHead
          stage="Learn"
          crumb={crumb}
          title="Is this you?"
          sub={`More than one business is called “${draft.question.business}”. It stopped to ask instead of guessing.`}
        />
        <AskCard q={draft.question} run={askRun} busy={busy} onAnswer={answer} />
      </>
    );
  } else if (draft?.profile) {
    body = (
      <>
        <FlowHead stage="Confirm" crumb={crumb} title={name} sub="Check each part. Your words outrank what it read." />
        <ProfileWalk profile={draft.profile} st={st} setSt={setSt} busy={busy} onConfirm={confirm} />
      </>
    );
  } else if (view.confirmed) {
    body = (
      <>
        <FlowHead
          crumb="Edit profile"
          title={name}
          sub="Change what is off. Every goal for it reads the new version."
          aside={
            <button type="button" className="vbtn" onClick={() => setVersions(true)}>
              <Icon name="clock" />
              Version {view.confirmed.version}
            </button>
          }
        />
        <ProfileWalk profile={view.confirmed.profile} st={st} setSt={setSt} busy={busy} onConfirm={() => void saveChanges()} edit />
        {versions && (
          <ProfileVersions
            productId={id}
            name={name}
            onto={view.confirmed.version}
            onClose={() => setVersions(false)}
            say={toast}
            onUsed={async (from, now) => {
              setVersions(false);
              setSt(emptyWalk());
              await load();
              toast(`Version ${from}'s words are back, as version ${now}. Every goal for ${name} reads them from now.`);
            }}
          />
        )}
      </>
    );
  } else {
    const stopped = view.learnRun && view.learnRun.state !== "done" ? view.learnRun : null;
    body = (
      <>
        <FlowHead
          stage="Learn"
          crumb={crumb}
          title={name}
          sub={stopped ? "The agent stopped before it finished. Try again." : "Flashadz has not learned this business yet."}
        />
        <section className="card">
          {stopped?.error && (
            <div className="cnote" style={{ marginTop: 0, marginBottom: 6 }}>
              <Icon name="info" />
              <span>{stopped.error}</span>
            </div>
          )}
          <label className="fl" style={stopped?.error ? undefined : { marginTop: 0 }} htmlFor="lsM">
            Anything you already have
          </label>
          <textarea
            className="field"
            id="lsM"
            rows={3}
            placeholder="Optional. Notes, an FAQ or a pitch. Your words outrank public pages."
            value={startNote}
            onChange={(e) => setStartNote(e.target.value)}
          />
        </section>
        <div className="wz-nav">
          <span className="sp" />
          <button type="button" className="btn" disabled={busy} onClick={start}>
            Learn the business
            <Icon name="right" />
          </button>
        </div>
      </>
    );
  }

  return (
    <section className="wrap ga addprod">
      {body}
      {toastNode}
    </section>
  );
}

/**
 * A product's profile flow (`/products/:id/profile`), after "Whose business is it?": the agent learning it
 * live, its question when it is not sure which business is yours, the walk to check and confirm the
 * draft, and the way to the first goal; once confirmed, the same walk changes it. Which step shows comes from
 * the product's profile and its run.
 */
export function ProductProfile() {
  const { id = "" } = useParams();
  const flow = useProfileFlow(id);
  return useNative() ? <ProfileNative flow={flow} /> : <ProfileDesktop flow={flow} />;
}
